/**
 * Debug Utility for the Filter Tool
 * Provides consistent logging with component context, timestamps, and log levels
 * Can be easily enabled/disabled through localStorage or environment variables
 */

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,   // Most detailed logging
  INFO: 1,    // General information
  WARN: 2,    // Warnings that don't break functionality
  ERROR: 3,   // Errors that impact functionality
  NONE: 4     // No logging
};

// Default configuration
let config = {
  enabled: true,           // Master on/off switch
  minLevel: LOG_LEVELS.DEBUG,  // Minimum level to show
  showTimestamp: true,     // Include timestamps
  showComponent: true,     // Include component name
  allowLocalStorage: true, // Allow configuration via localStorage
  consoleOutput: true,     // Log to console
  sessionStorage: true,    // Store logs in sessionStorage for UI retrieval
  maxStoredLogs: 1000,     // Maximum number of logs to keep in storage
  colorize: true,          // Add colors in console
};

// Check for localStorage configuration
const initConfig = () => {
  try {
    if (localStorage.getItem('filterTool_debug') !== null) {
      const storedConfig = JSON.parse(localStorage.getItem('filterTool_debug'));
      config = { ...config, ...storedConfig };
    }
    
    // Also check if there's a URL parameter to enable debugging
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('debug')) {
      config.enabled = urlParams.get('debug') !== 'false';
    }
  } catch (e) {
    // Silently fail and use defaults
  }
};

// Initialize on load
initConfig();

// Store logs in sessionStorage if enabled
const storeLogs = [];

// Colors for different log levels
const COLORS = {
  DEBUG: 'color: #6c757d', // Grey
  INFO: 'color: #0095FF',  // Wyspur blue
  WARN: 'color: #FFD700',  // Wyspur yellow
  ERROR: 'color: #E83E8C', // Wyspur pink
};

/**
 * Format and output a log message
 * @param {string} level - Log level
 * @param {string} component - Component name
 * @param {string} message - Log message
 * @param {any} data - Additional data to log
 */
const logMessage = (level, component, message, data) => {
  // Check if logging is enabled and meets minimum level
  if (!config.enabled || LOG_LEVELS[level] < config.minLevel) {
    return;
  }

  // Format the log message
  let formattedMessage = '';
  
  // Add timestamp if enabled
  if (config.showTimestamp) {
    formattedMessage += `[${new Date().toISOString()}] `;
  }
  
  // Add level
  formattedMessage += `[${level}] `;
  
  // Add component name if enabled
  if (config.showComponent && component) {
    formattedMessage += `[${component}] `;
  }
  
  // Add the actual message
  formattedMessage += message;
  
  // Store log in session storage if enabled
  if (config.sessionStorage) {
    storeLogs.push({
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data: data !== undefined ? JSON.stringify(data) : undefined
    });
    
    // Trim logs if they exceed maximum
    if (storeLogs.length > config.maxStoredLogs) {
      storeLogs.shift();
    }
    
    // Update sessionStorage
    try {
      sessionStorage.setItem('filterTool_logs', JSON.stringify(storeLogs));
    } catch (e) {
      // Silently fail if storage is full
    }
  }
  
  // Output to console if enabled
  if (config.consoleOutput) {
    if (config.colorize && COLORS[level]) {
      console.log(`%c${formattedMessage}`, COLORS[level], data !== undefined ? data : '');
    } else {
      console.log(formattedMessage, data !== undefined ? data : '');
    }
  }
};

/**
 * Debug logger factory that creates logging functions for a specific component
 * @param {string} componentName - Name of the component for context
 * @returns {Object} - Object with logging methods
 */
export const createLogger = (componentName) => {
  return {
    debug: (message, data) => logMessage('DEBUG', componentName, message, data),
    info: (message, data) => logMessage('INFO', componentName, message, data),
    warn: (message, data) => logMessage('WARN', componentName, message, data),
    error: (message, data) => logMessage('ERROR', componentName, message, data),
    
    // Log state changes (commonly used pattern)
    state: (action, prevState, nextState) => {
      logMessage(
        'DEBUG', 
        componentName, 
        `State change from ${action}`, 
        { 
          prev: prevState,
          next: nextState,
          changed: Object.keys(nextState).filter(key => 
            JSON.stringify(prevState[key]) !== JSON.stringify(nextState[key])
          )
        }
      );
    },
    
    // Log prop changes
    props: (prevProps, nextProps) => {
      logMessage(
        'DEBUG',
        componentName,
        'Props changed',
        {
          prev: prevProps,
          next: nextProps,
          changed: Object.keys(nextProps).filter(key => 
            JSON.stringify(prevProps[key]) !== JSON.stringify(nextProps[key])
          )
        }
      );
    },
    
    // Log lifecycle events
    lifecycle: (event) => logMessage('DEBUG', componentName, `Lifecycle: ${event}`),
    
    // Log render events
    render: (reason) => logMessage('DEBUG', componentName, `Render${reason ? ': ' + reason : ''}`)
  };
};

/**
 * Configure the debug utility
 * @param {Object} newConfig - Configuration options to apply
 */
export const configureDebug = (newConfig) => {
  config = { ...config, ...newConfig };
  
  // Save to localStorage if enabled
  if (config.allowLocalStorage) {
    try {
      localStorage.setItem('filterTool_debug', JSON.stringify(config));
    } catch (e) {
      // Silently fail
    }
  }
  
  return config;
};

/**
 * Enable or disable all debugging
 * @param {boolean} enabled - Whether debugging should be enabled
 */
export const enableDebug = (enabled = true) => {
  configureDebug({ enabled });
};

/**
 * Set the minimum log level
 * @param {string} level - Minimum level to log (DEBUG, INFO, WARN, ERROR)
 */
export const setLogLevel = (level) => {
  if (LOG_LEVELS[level] !== undefined) {
    configureDebug({ minLevel: LOG_LEVELS[level] });
  }
};

/**
 * Get all stored logs
 * @returns {Array} - Array of stored log entries
 */
export const getLogs = () => {
  return [...storeLogs];
};

/**
 * Clear all stored logs
 */
export const clearLogs = () => {
  storeLogs.length = 0;
  try {
    sessionStorage.removeItem('filterTool_logs');
  } catch (e) {
    // Silently fail
  }
};

// Export log levels for external use
export const LEVELS = { ...LOG_LEVELS };

// Export a default logger for quick use
export default createLogger('App');
